import { FC, useMemo, useContext } from "react";

import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { SessionKpi } from "@/features/training/model/Training/TrainingDrillSession";
import {
	Box,
	Chip,
	LinearProgress,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


interface SessionKpiParametersProps {
	kpis: SessionKpi[];
	compact?: boolean;
}

export const SessionKpiParameters: FC<SessionKpiParametersProps> = ({
	kpis,
	compact = false,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();

	if (!kpis?.length) return null;

	const fmt = (v: unknown) => {
		const n = Number(v);
		return Number.isFinite(n)
			? new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n)
			: "—";
	};

	const parse = (v: unknown) => {
		const n = Number(v);
		return Number.isFinite(n) ? n : null;
	};

	// Simple compliance summary
	const { passCount, withNumbers } = useMemo(() => {
		let pass = 0;
		let count = 0;
		kpis.forEach((k) => {
			const val = parse(k.Value);
			const max = parse(k.MaxValue);
			if (val !== null && max !== null) {
				count += 1;
				if (val <= max) pass += 1;
			}
		});
		return { passCount: pass, withNumbers: count };
	}, [kpis]);

	return (
		<Box mt={compact ? 0 : 4}>
			<Paper
				variant="outlined"
				sx={{
					borderRadius: 3,
					overflow: "hidden",
					background: theme.palette.background.paper,
				}}
			>
				<Box
					sx={(t) => ({
						px: 2.5,
						py: 1.15,
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						gap: 1,
						flexWrap: "wrap",
						borderBottom: 1,
						borderColor: alpha(t.palette.divider, 0.85),
						bgcolor:
							t.palette.mode === "dark"
								? alpha(t.palette.common.white, 0.02)
								: "grey.50",
					})}
				>
					<Box>
						<Typography
							variant="caption"
							sx={{
								display: "block",
								mb: 0.05,
								color: "primary.main",
								fontWeight: 700,
								letterSpacing: "0.08em",
								textTransform: "uppercase",
							}}
						>
							<Translate String="Training.DrillSession.KpiParameters.Overline">
								Performance
							</Translate>
						</Typography>
						<Typography variant="subtitle1" fontWeight={700}>
							<Translate String="Training.DrillSession.KpiParameters.Title">
								KPI Parameters
							</Translate>
						</Typography>
					</Box>

					<Stack direction="row" gap={0.75} alignItems="center" useFlexGap flexWrap="wrap">
						<Chip
							size="small"
							label={`${kpis.length} parameter${kpis.length === 1 ? "" : "s"}`}
							sx={{
								height: 26,
								borderRadius: 2,
								bgcolor: alpha(theme.palette.primary.main, 0.08),
								fontWeight: 700,
							}}
						/>
						<Chip
							size="small"
							label={`Within limit ${withNumbers > 0 ? `${passCount}/${withNumbers}` : "—"}`}
							sx={{
								height: 26,
								borderRadius: 2,
								bgcolor: alpha(theme.palette.success.main, 0.08),
								fontWeight: 700,
							}}
						/>
					</Stack>
				</Box>

				<TableContainer sx={{ maxHeight: 420 }}>
					<Table stickyHeader size="small" aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.DrillTab.PanelRight.DrillSession.SessionKpiParameters.L138C50.KpiTable", "kpi table")}>
						<TableHead>
							<TableRow
								sx={{
									backgroundColor:
										theme.palette.mode === "light"
											? alpha(theme.palette.primary.main, 0.04)
											: alpha(theme.palette.common.white, 0.03),
								}}
							>
								<TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap", px: 2, py: 1.25 }}>
									<Translate String="Training.Drill.KpiParameters.Name">
										Name
									</Translate>
								</TableCell>
								<TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap", px: 2, py: 1.25 }}>
									<Translate String="Training.DrillSession.KpiParameters.Value">
										Value
									</Translate>
								</TableCell>
								<TableCell
									align="right"
									sx={{ fontWeight: 700, whiteSpace: "nowrap", px: 2, py: 1.25 }}
								>
									<Translate String="Training.Drill.KpiParameters.MaxValue">
										Max Value
									</Translate>
								</TableCell>
								<TableCell
									align="right"
									sx={{ fontWeight: 700, whiteSpace: "nowrap", px: 2, py: 1.25 }}
								>
									<Translate String="Training.DrillSession.KpiParameters.Status">
										Status
									</Translate>
								</TableCell>
							</TableRow>
						</TableHead>

						<TableBody>
							{kpis.map((row) => {
								const val = parse(row.Value);
								const max = parse(row.MaxValue);
								const pct =
									val !== null && max !== null && max > 0
										? Math.min((val / max) * 100, 100)
										: null;
								const withinLimit =
									val !== null && max !== null ? val <= max : undefined;

								return (
									<TableRow
										key={row.Id}
										hover
										sx={{
											"&:nth-of-type(odd)": {
												backgroundColor: alpha(theme.palette.action.hover, 0.1),
											},
										}}
									>
										<TableCell sx={{ fontWeight: 600, px: 2, py: 1.4 }}>
											{row.Name}
										</TableCell>

										<TableCell sx={{ minWidth: 220, px: 2, py: 1.4 }}>
											{val === null ? (
												"—"
											) : (
												<Tooltip
													title={
														pct !== null
															? `${fmt(val)} / ${fmt(max)} (${pct.toFixed(0)}%)`
															: fmt(val)
													}
													enterDelay={400}
												>
													<Box>
														<Typography variant="body2" sx={{ mb: 0.5 }}>
															{fmt(val)}
														</Typography>
														{pct !== null && (
															<LinearProgress
																variant="determinate"
																value={pct}
																sx={{
																	height: 6,
																	borderRadius: 999,
																	bgcolor: alpha(
																		theme.palette.text.disabled,
																		0.15,
																	),
																}}
															/>
														)}
													</Box>
												</Tooltip>
											)}
										</TableCell>

										<TableCell align="right" sx={{ whiteSpace: "nowrap", px: 2, py: 1.4 }}>
											<Chip
												label={fmt(max)}
												color="info"
												size="small"
												variant="outlined"
												sx={{ fontWeight: 700, borderRadius: 2 }}
											/>
										</TableCell>

										<TableCell align="right" sx={{ whiteSpace: "nowrap", px: 2, py: 1.4 }}>
											{withinLimit === undefined ? (
												<Chip size="small" label="—" variant="outlined" sx={{ borderRadius: 2 }} />
											) : withinLimit ? (
												<Chip
													size="small"
													color="success"
													label={
														<Translate String="Training.DrillSession.KpiParameters.WithinLimit">
														Within limit
													</Translate>
												}
													sx={{ fontWeight: 700, borderRadius: 2 }}
												/>
											) : (
												<Chip
													size="small"
													color="warning"
													label={
														<Translate String="Training.DrillSession.KpiParameters.Exceeded">
														Exceeded
													</Translate>
												}
													sx={{ fontWeight: 700, borderRadius: 2 }}
												/>
											)}
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>
		</Box>
	);
};
